import type * as React from 'react';
import { useEffect } from 'react';

import { LanguageProvider } from '../../context/LanguageContext';
import CookieBanner from '../CookieBanner/CookieBanner';
import Footer from '../Footer/Footer';
import NavigationBar from '../NavigationBar/NavigationBar';
import TopBar from '../TopBar/TopBar';
import TransitionLayout from '../TransitionLayout/TransitionLayout';
import styles from './Layout.module.css';

export interface LayoutProps {
  children?: React.ReactNode;
  pageContext: {
    lang?: 'de' | 'en';
    slug?: string;
    alternateSlug?: string;
  };
  location: {
    pathname: string;
  };
}

/**
 * Layout component that wraps all pages.
 *
 * Responsibilities:
 * - Sets the `lang` attribute on the HTML element based on pageContext.lang
 * - Provides LanguageContext to all children
 * - Renders TopBar, NavigationBar, children (via TransitionLayout), and Footer
 * - Uses semantic HTML structure (header, main, footer)
 */
const Layout: React.FC<LayoutProps> = ({ children, pageContext, location }) => {
  const lang = pageContext?.lang || (location.pathname.startsWith('/en/') ? 'en' : 'de');
  const alternateSlug = pageContext?.alternateSlug || (lang === 'de' ? '/en/' : '/');
  const currentPath = location.pathname;
  // Landing page ends in a full-bleed parallax divider that should sit flush to
  // the footer; every other page wants breathing room before it.
  const homePath = lang === 'de' ? '/' : '/en/';
  const isHome = (currentPath.endsWith('/') ? currentPath : `${currentPath}/`) === homePath;

  // Set the lang attribute on the <html> element
  useEffect(() => {
    document.documentElement.lang = lang;
  }, [lang]);

  return (
    <LanguageProvider lang={lang} alternateSlug={alternateSlug}>
      <div className={styles.layout}>
        <header className={styles.header}>
          <TopBar lang={lang} />
          <NavigationBar lang={lang} currentPath={currentPath} />
        </header>
        <main className={`${styles.main}${isHome ? '' : ` ${styles.mainSpaced}`}`}>
          <TransitionLayout location={currentPath}>{children}</TransitionLayout>
        </main>
        <Footer lang={lang} />
        <CookieBanner lang={lang} />
      </div>
    </LanguageProvider>
  );
};

export default Layout;
